import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Crown } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { useArticlePath } from "@/lib/i18n";
import { MiniRing, PanelCTA } from "@/components/layernav/primitives";
import { trackEvent } from "@/hooks/useArticleAnalytics";

export const RailModule = ({ label, cyan, children, testid, className = "", track }: {
  label: string; cyan?: boolean; children: ReactNode; testid: string; className?: string; track?: string;
}) => (
  <section data-testid={testid} data-track={track}
    className={`rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4 ${className}`}>
    <p className={`font-mono text-[9px] font-bold uppercase tracking-[0.28em] ${cyan ? "text-[#00E5FF]" : "text-[#A78BFA]"}`}>
      {label}
    </p>
    <div className="mt-3">{children}</div>
  </section>
);

export const GlobalMarketContext = () => {
  const { data: global } = useQuery({
    queryKey: ["markets-global"],
    queryFn: async () => (await api.get("/markets/global")).data,
    staleTime: 10 * 60 * 1000,
  });
  if (!global?.available) return null;
  const fmtBig = (n?: number) => {
    if (!n) return "—";
    if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    return `$${(n / 1e6).toFixed(0)}M`;
  };
  const rows: [string, ReactNode][] = [
    ["Total Market Cap", (
      <span key="m" className="flex items-center gap-2">
        <span className="font-mono text-[13px] text-white">{fmtBig(global.total_market_cap_usd)}</span>
        {typeof global.market_cap_change_24h_pct === "number" && (
          <span className={`font-mono text-[10px] ${global.market_cap_change_24h_pct >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
            {global.market_cap_change_24h_pct >= 0 ? "+" : ""}{global.market_cap_change_24h_pct.toFixed(2)}%
          </span>
        )}
      </span>
    )],
    ["24H Volume", <span key="v" className="font-mono text-[13px] text-white">{fmtBig(global.volume_24h_usd)}</span>],
  ];
  if (typeof global.btc_dominance === "number") {
    rows.push(["BTC Dominance",
      <span key="d" className="font-mono text-[13px] text-white">{global.btc_dominance.toFixed(1)}%</span>]);
  }
  return (
    <RailModule label="Market Context" cyan testid="market-context-global" track="context_global_market">
      <div className="space-y-2.5">
        {rows.map(([k, v]) => (
          <div key={k as string} className="flex items-center justify-between gap-3">
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">{k}</span>
            {v}
          </div>
        ))}
      </div>
      {global.updated_at && (
        <p className="mt-3 pt-2.5 border-t border-white/[0.06] font-mono text-[9px] uppercase tracking-wider text-gray-600">
          Updated {timeAgo(global.updated_at)}
        </p>
      )}
    </RailModule>
  );
};

const REG_STATUS_COLOR: Record<string, string> = {
  enforced: "text-[#34D399] border-[#34D399]/30",
  enacted: "text-[#34D399] border-[#34D399]/30",
  proposed: "text-[#60A5FA] border-[#60A5FA]/30",
  in_review: "text-[#00E5FF] border-[#00E5FF]/30",
  review: "text-[#00E5FF] border-[#00E5FF]/30",
};

export const RegulationContext = ({ reg }: {
  reg?: { slug: string; title: string; countries: string[]; agencies: string[];
          status?: string; impact_level?: string; updated_at?: string } | null;
}) => {
  if (!reg) {
    return (
      <RailModule label="Regulation Context" cyan testid="regulation-context" track="context_regulation">
        <p className="text-[13px] text-gray-400 leading-snug">
          Track live crypto policy developments across jurisdictions.
        </p>
        <div className="mt-3">
          <PanelCTA to="/regulation-tracker" testid="regulation-tracker-cta">Open Regulation Tracker</PanelCTA>
        </div>
      </RailModule>
    );
  }
  const statusCls = REG_STATUS_COLOR[(reg.status || "").toLowerCase()] || "text-gray-400 border-white/15";
  return (
    <RailModule label="Regulation Context" cyan testid="regulation-context" track="context_regulation">
      <Link to={`/regulation/${reg.slug}`} data-testid="regulation-context-title"
        className="block text-[14px] font-bold text-white leading-snug line-clamp-2 hover:text-[#00E5FF] transition-colors">
        {reg.title}
      </Link>
      <div className="mt-3 space-y-2">
        {reg.countries.length > 0 && (
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">Jurisdiction</span>
            <span className="text-[12px] text-gray-300 text-right">{reg.countries.join(", ")}</span>
          </div>
        )}
        {reg.agencies.length > 0 && (
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">Regulator</span>
            <span className="text-[12px] text-gray-300 text-right">{reg.agencies.join(", ")}</span>
          </div>
        )}
        {reg.status && (
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">Status</span>
            <span className={`font-mono text-[9px] font-bold uppercase tracking-widest border rounded px-1.5 py-0.5 ${statusCls}`}>
              {reg.status.replace(/_/g, " ")}
            </span>
          </div>
        )}
        {reg.updated_at && (
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">Updated</span>
            <span className="font-mono text-[11px] text-gray-400">{timeAgo(reg.updated_at)}</span>
          </div>
        )}
      </div>
      <div className="mt-3.5 pt-3 border-t border-white/[0.06]">
        <PanelCTA to="/regulation-tracker" testid="regulation-tracker-cta">Open Regulation Tracker</PanelCTA>
      </div>
    </RailModule>
  );
};

export const SecurityContext = ({ sc }: {
  sc?: { ecosystem?: string | null; providers?: string[]; mitigation?: string | null } | null;
}) => {
  if (!sc || (!sc.ecosystem && !(sc.providers || []).length && !sc.mitigation)) return null;
  const rows: [string, string][] = [];
  if (sc.ecosystem) rows.push(["Affected ecosystem", sc.ecosystem]);
  if (sc.providers?.length) rows.push(["Affected providers", sc.providers.slice(0, 4).join(" · ")]);
  if (sc.mitigation) rows.push(["Mitigation", sc.mitigation]);
  return (
    <RailModule label="Security Context" cyan testid="security-context" track="context_security">
      <div className="space-y-2.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-3">
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500 shrink-0 mt-0.5">{k}</span>
            <span className="text-[12px] text-gray-200 text-right leading-snug">{v}</span>
          </div>
        ))}
      </div>
    </RailModule>
  );
};

export const LayerIndexModule = () => {
  const { data: li } = useQuery({
    queryKey: ["layer-index"],
    queryFn: async () => (await api.get("/layer-index")).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: hist } = useQuery({
    queryKey: ["layer-index-history-7"],
    queryFn: async () => (await api.get("/layer-index/history?days=7")).data,
    staleTime: 10 * 60 * 1000,
  });
  if (!li) return null;
  const days = hist?.days || [];
  const delta = days.length >= 2 ? days[days.length - 1].value - days[days.length - 2].value : null;
  return (
    <RailModule label="Layer Index" testid="rail-layer-index">
      <MiniRing value={li.value ?? 50} label={li.label || "Neutral"} />
      {delta !== null && delta !== 0 && (
        <p className={`mt-2.5 font-mono text-[11px] ${delta > 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}
          data-testid="layer-index-delta">
          {delta > 0 ? "↑" : "↓"} {Math.abs(delta)} pts in 24h
        </p>
      )}
    </RailModule>
  );
};

export const WhyThisMatters = ({ text }: { text?: string }) => {
  if (!text) return null;
  return (
    <RailModule label="Why This Matters" testid="rail-why-this-matters" track="why_this_matters">
      <p className="text-[13px] text-gray-300 leading-relaxed">{text}</p>
    </RailModule>
  );
};

const TYPE_SHORT: Record<string, string> = {
  deep_dive: "Deep Dive", market_brief: "Market Update", imported: "External Reporting",
  editorial: "Original", author: "Contributor", pr: "Press Release",
};
const TYPE_PRIORITY: Record<string, number> = { deep_dive: 0, editorial: 1, author: 2, market_brief: 3, imported: 4, pr: 5 };

export const pickIntelligence = (related: Article[], exclude?: Set<string>): Article[] => {
  // Relevance over fullness (§15/§21): no fallback padding — 0-3 items.
  // Family-true items outrank generic entity matches for family stories.
  const items = (related || []).filter((a) => !exclude?.has(a.slug));
  return [...items].sort((a, b) => {
    const pa = (a as any).pinned ? 1 : 0, pb = (b as any).pinned ? 1 : 0;
    if (pa !== pb) return pb - pa;
    const fa = (a as any).family_match ? 1 : 0, fb = (b as any).family_match ? 1 : 0;
    if (fa !== fb) return fb - fa;
    const sa = (a as any).match_score ?? 0, sb = (b as any).match_score ?? 0;
    if (sa !== sb) return sb - sa;
    return (TYPE_PRIORITY[a.content_type || "imported"] ?? 4) - (TYPE_PRIORITY[b.content_type || "imported"] ?? 4);
  }).slice(0, 3);
};

export const RelatedIntelligence = ({ items, category }: {
  items: Article[]; category?: string;
}) => {
  const toArticle = useArticlePath();
  if (!items.length) return null;
  return (
    <RailModule label="Related Intelligence" cyan testid="rail-related-intelligence">
      <div className="space-y-3.5">
        {items.map((a, i) => (
          <Link key={a.slug} to={toArticle(a.slug)} data-testid={`related-intel-${i}`}
            onClick={() => trackEvent("related_intelligence_click", { slug: a.slug })}
            className="group flex items-start gap-3">
            {i === 0 && a.hero_image_url && (
              <img src={a.hero_image_url} alt="" loading="lazy"
                className="h-12 w-16 shrink-0 rounded-md object-cover border border-white/10" />
            )}
            <span className="min-w-0">
              <span className="block font-mono text-[8px] font-bold uppercase tracking-widest text-[#A78BFA]">
                {TYPE_SHORT[a.content_type || ""] || "Story"}
              </span>
              <span className="mt-0.5 block text-[13px] font-semibold text-gray-200 group-hover:text-white leading-snug line-clamp-2 transition-colors">
                {a.title}
              </span>
            </span>
          </Link>
        ))}
      </div>
      {category && (
        <Link to={`/${category}`} data-testid="related-intel-more"
          className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-[#00E5FF] hover:text-white transition-colors">
          View More <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </RailModule>
  );
};

export const GoDeeper = ({ pick }: {
  pick?: { slug: string; title: string; description?: string; content_type?: string } | null;
}) => {
  const { user } = useAuth();
  const toArticle = useArticlePath();
  if (!pick) return null;
  const isPremium = !!user?.is_premium;
  const ctaLabel = isPremium ? "Open Report" : user ? "Go Premium" : "Unlock Premium";
  const ctaTo = isPremium ? toArticle(pick.slug) : "/premium";
  return (
    <RailModule label="Go Deeper" cyan testid="rail-go-deeper" track="go_deeper"
      className="relative overflow-hidden !border-[#7C3AED]/40 !bg-gradient-to-br !from-[#130b20] !to-[#0a0b10]">
      <span aria-hidden className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-[#00E5FF]/[0.07] blur-2xl" />
      <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-500">
        <Crown className="h-3 w-3 text-[#A78BFA]" /> Premium {TYPE_SHORT[pick.content_type || ""] || "Analysis"}
      </p>
      <Link to={toArticle(pick.slug)}
        className="mt-1.5 block font-news font-bold text-white text-[15px] leading-snug hover:text-[#00E5FF] transition-colors">
        {pick.title}
      </Link>
      {pick.description && (
        <p className="mt-1.5 text-xs text-gray-500 leading-snug line-clamp-3">{pick.description}</p>
      )}
      <Link to={ctaTo} data-testid="go-deeper-cta"
        onClick={() => trackEvent("go_deeper_click", { slug: pick.slug, state: ctaLabel })}
        className="mt-3.5 inline-flex items-center gap-2 rounded-md bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-[13px] font-bold px-3.5 py-2 transition-colors">
        {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </RailModule>
  );
};
